<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row">
      <div class="col-sm-4">
        <h4 qid="measures-page-title" class="float-left">
          {{ $t('maintenance.measure_categories.title') }}
        </h4>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="measures-create-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <button
            class="btn btn-success float-right"
            @click="showCreateModal"
            qid="measures-create-button"
            v-if="isGranted(permissions.MEASURE_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.measure_categories.create_button') }}
        </button>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-p-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.measure_categories.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-p-name"
              >
                {{ $t('maintenance.measures.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th scope="col">
                {{ $t('projects.table.scope') }}
              </th>
              <th width="200"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <span @click="showViewModal(object)" class="pointer">
                    {{ object.name }}
                  </span>
                </td>
                <td>
                  <span v-if="object.scope">
                    <span class="mr-1 badge badge-info mb-1" v-for="type in object.scope">
                      {{ $t('risk_scenarios.type.' + $options.filters.slugify(type)) }}
                    </span>
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.MEASURE_CATEGORY_EDIT, permissions.MEASURE_CATEGORY_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-p-modal"
                        @click="showEditPModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span @click="showViewModal(object)"
                        qid="view-modal"
                        class="btn btn-view"
                  >
                    <font-awesome-icon icon="eye"/>
                  </span>
                  <span v-if="isGranted([permissions.MEASURE_CATEGORY_DELETE, permissions.MEASURE_CATEGORY_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-p-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('projects.create.scope_label') }}
              </label>
              <multiselect
                  v-model="filters.scope"
                  :options="scopes"
                  :placeholder="$t('projects.create.scope_placeholder')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="filter-p-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Policy confirm modal template -->
    <b-modal ref="delete-category" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-p-title">
          {{ $t('maintenance.measure_categories.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.measure_categories.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.measure_categories.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-p-cancel-button"
        >
          {{ $t('maintenance.measure_categories.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-p-submit-button"
                  v-if="isGranted([permissions.MEASURE_CATEGORY_DELETE, permissions.MEASURE_CATEGORY_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.measure_categories.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit Policy modal template -->
    <b-modal ref="edit-category" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-p-title">
          {{ $t('maintenance.measure_categories.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editPFormSubmit" qid="edit-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-p-name-label">
              {{ $t('maintenance.measure_categories.create.name_label') }}
            </label>
            <span class="text-red">*</span>
            <b-form-input
              qid="edit-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.measures.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-p-implementation-status-label">
              {{ $t('maintenance.measure_categories.create.scope_label') }}
            </label>
            <span class="text-red">*</span>
            <multiselect
                v-model="selectedScopes"
                :options="scopes"
                :multiple="true"
                track-by="value"
                :taggable="true"
                :placeholder="$t('maintenance.measure_categories.create.scope_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-p-form-implementation-status"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditPModal"
                    qid="edit-p-cancel-button"
          >
            {{ $t('maintenance.measures.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.measures.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Create measure category -->
    <b-modal ref="create-category" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-p-title">
          {{ $t('maintenance.measure_categories.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="createFormSubmit" qid="edit-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-p-name-label">
              {{ $t('maintenance.measure_categories.create.name_label') }}
            </label>
            <span class="text-red">*</span>
            <b-form-input
              qid="edit-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.measure_categories.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-p-implementation-status-label">
              {{ $t('maintenance.measure_categories.create.scope_label') }}
            </label>
            <span class="text-red">*</span>
            <multiselect
                v-model="selectedScopes"
                :options="scopes"
                :multiple="true"
                track-by="value"
                :taggable="true"
                :placeholder="$t('maintenance.measure_categories.create.scope_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-p-form-implementation-status"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateModal"
                    qid="edit-p-cancel-button"
          >
            {{ $t('maintenance.measures.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.measure_categories.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- View measure category -->
    <b-modal ref="view-category" hide-footer @hide="hideViewModal" v-if="selectedCategory">
      <div class="d-block text-center">
        <h4 qid="update-tp-title">
          {{ $t('maintenance.measure_categories.view.title') }}
        </h4>
      </div>

      <label class="mt-3">
        {{ $t('maintenance.measure_categories.create.name_label') }}
      </label>
      <br>
      <span v-if="selectedCategory.name" qid="policy-name">
        {{ selectedCategory.name }}
      </span>
      <br>

      <label class="mt-3" v-if="selectedCategory && selectedCategory.scope">
        {{ $t('maintenance.measure_categories.create.scope_label') }}
      </label>
      <br>
      <span v-if="selectedCategory && selectedCategory.scope" qid="policy-category">
        <span v-if="selectedCategory.scope">
          <span class="mr-1 badge badge-info mb-1" v-for="type in selectedCategory.scope">
            {{ $t('risk_scenarios.type.' + $options.filters.slugify(type)) }}
          </span>
        </span>
      </span>
      <br>

      <b-button class="mt-4 float-right"
                variant="success"
                @click="hideViewModal"
                qid="create-p-cancel-button"
      >
        {{ $t('processes.policies.view.close') }}
      </b-button>
      <br>

    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  export default {
    name: 'Measures',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.measures.title'),
            to: '/maintenance/measures'
          },
          {
            text: this.$t('maintenance.measure_categories.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        pForm: {
          name: '',
          scope: []
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          scope: '',
          keywordValue: ''
        },
        categoryItems: [],
        categoryInput: '',
        scopes: [
          { name: this.$t('projects.scope.activity'), value: 'activity' },
          { name: this.$t('projects.scope.third_party'), value: 'third_party' }
        ],
        loading: true,
        buttonDisabled: false,
        myGuides: [],
        selectedCategory: {},
        selectedScopes: []
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      resetFilters() {
        this.filters.scope = '',
        this.debounceInput()
      },
      handleCategoryInput(input) {
        this.categoryInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.scope && this.filters.scope.value)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('measure-categories', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            scope: this.filters.scope ? this.filters.scope.value : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-category'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-category'].hide()
      },
      showViewModal(object) {
        this.selectedCategory = object
        this.$refs['view-category'].show()
      },
      hideViewModal() {
        this.selectedCategory = {}
        this.$refs['view-category'].hide()
      },
      showCreateModal() {
        this.pForm.name = ''
        this.pForm.scope = []
        this.selectedScopes = []
        this.error = ''
        this.$refs['create-category'].show()
      },
      hideCreateModal() {
        this.pForm.name = ''
        this.pForm.scope = []
        this.$refs['create-category'].hide()
      },
      createFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        let scopes = []

        // Append list of types
        if (this.selectedScopes && this.selectedScopes.length) {
          for (var i = 0; i < this.selectedScopes.length; i++) {
            scopes.push(this.selectedScopes[i].value)
          }
        }

        this.pForm.client = this.getClient.slug;
        this.pForm.scope = scopes

        if (this.pForm.scope.length) {
          piincHttp.post('measure-categories', this.pForm).then(function(response) {
            self.hideCreateModal();
            self.loadContent(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.measure_categories.create.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = this.$t('system.scope_error_message')
        }
      },
      showEditPModal(object) {
        let self = this;

        self.selectedScopes = [];
        this.error = ''
        if (object.scope && object.scope.length) {

          _.forEach(object.scope, function (value, key) {
            _.find(self.scopes, function(item) {
              if (item.value === value) {
                self.selectedScopes.push(item)
              }
            });
          })
        }

        this.pForm.slug = object.slug;
        this.pForm.name = object.name;
        this.$refs['edit-category'].show()
      },
      hideEditPModal() {
        this.error = ''
        this.$refs['edit-category'].hide()
      },
      editPFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        let scopes = []

        // Append list of types
        if (this.selectedScopes && this.selectedScopes.length) {
          for (var i = 0; i < this.selectedScopes.length; i++) {
            scopes.push(this.selectedScopes[i].value)
          }
        }

        this.pForm.client = this.getClient.slug;
        this.pForm.scope = scopes

        if (this.pForm.scope.length) {
          piincHttp.put('measure-categories/' + this.pForm.slug, this.pForm).then(function(response) {
            self.hideEditPModal();
            self.loadContent(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.measure_categories.update.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = this.$t('system.scope_error_message')
        }
      },
      confirmDeleteP(object) {
        let self = this;
        piincHttp.delete('measure-categories/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.measure_categories.delete.success'))
        }, function() {});
      }
    },
    components: {

    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'measure_categories',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.measure_categories) {
          this.filters = this.getFilters.measure_categories
        }
        this.loadContent();

        if (this.getClient.systemModule) {
          this.scopes.push({ name: this.$t('projects.scope.system'), value: 'system' },)
        }
      }
    }
  }
</script>
